import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

/**
 * Extract a readable upload error message from raw HTTP or Resumable.js responses.
 */
const extractUploadErrorMessage = (message, fallbackMessage = 'Upload failed.') => {
    if (typeof message !== 'string' || message.trim() === '') {
        return fallbackMessage;
    }

    try {
        const parsedMessage = JSON.parse(message);
        return parsedMessage?.message ?? fallbackMessage;
    } catch (error) {
        return message;
    }
};

/**
 * Preserve the OCR field order supplied by the backend when rendering tables.
 */
const getOrderedFieldEntries = (structuredDocument) => {
    if (Array.isArray(structuredDocument?.field_entries)) {
        return structuredDocument.field_entries;
    }

    return Object.entries(structuredDocument?.fields ?? {}).map(([name, value]) => ({ name, value }));
};

const OcrDocumentsPage = ({
    listUrl,
    uploadUrl,
    detailsUrlTemplate,
    managedFilesUsageBytes,
    managedFilesUsageLabel,
    managedFilesLimitBytes,
    managedFilesLimitLabel,
}) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [usageBytes, setUsageBytes] = useState(managedFilesUsageBytes ?? 0);
    const [usageLabel, setUsageLabel] = useState(managedFilesUsageLabel ?? '0 B');
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingId, setDeletingId] = useState('');
    const [resumableReady, setResumableReady] = useState(false);
    const inputRef = useRef(null);
    const browseButtonRef = useRef(null);
    const resumableRef = useRef(null);
    const csrfToken = useMemo(() => getCsrfToken(), []);

    const loadDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(listUrl, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Unable to load OCR documents.');
            }

            const payload = await response.json();
            setDocuments(Array.isArray(payload?.data) ? payload.data : []);
            setError('');
        } catch (requestError) {
            setError(requestError.message ?? 'Unable to load OCR documents.');
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [listUrl]);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const handlePickFiles = useCallback(() => {
        inputRef.current?.click();
    }, []);

    const uploadFilesDirectly = useCallback(async (files) => {
        if (!files.length) {
            return;
        }

        setUploading(true);
        setError('');

        for (const file of files) {
            await new Promise((resolve) => {
                const xhr = new XMLHttpRequest();
                const formData = new FormData();
                formData.append('file', file);

                xhr.open('POST', uploadUrl, true);
                xhr.withCredentials = true;
                xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken);
                xhr.setRequestHeader('Accept', 'application/json');
                xhr.upload.onprogress = (uploadEvent) => {
                    if (!uploadEvent.lengthComputable) {
                        return;
                    }

                    setUploadProgress(Math.round((uploadEvent.loaded / uploadEvent.total) * 100));
                };
                xhr.onload = async () => {
                    try {
                        const payload = JSON.parse(xhr.responseText || '{}');
                        if (xhr.status < 200 || xhr.status >= 300) {
                            throw new Error(payload?.message ?? 'Upload failed.');
                        }

                        if (payload?.managed_files_usage_bytes !== undefined) {
                            setUsageBytes(payload.managed_files_usage_bytes);
                        }
                        if (payload?.managed_files_usage_label) {
                            setUsageLabel(payload.managed_files_usage_label);
                        }
                        await loadDocuments();
                    } catch (uploadError) {
                        setError(uploadError.message ?? 'Upload failed.');
                    } finally {
                        setUploadProgress(0);
                        resolve();
                    }
                };
                xhr.onerror = () => {
                    setError('Upload failed.');
                    setUploadProgress(0);
                    resolve();
                };
                xhr.send(formData);
            });
        }

        setUploading(false);
    }, [csrfToken, loadDocuments, uploadUrl]);

    const handleUpload = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        await uploadFilesDirectly(files);
        event.target.value = '';
    }, [uploadFilesDirectly]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.Resumable || !browseButtonRef.current) {
            setResumableReady(false);
            return undefined;
        }

        const resumable = new window.Resumable({
            target: uploadUrl,
            chunkSize: 1 * 1024 * 1024,
            simultaneousUploads: 2,
            testChunks: false,
            throttleProgressCallbacks: 1,
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            query: {
                _token: csrfToken,
            },
            fileType: ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tif', 'tiff'],
        });

        if (!resumable.support) {
            setResumableReady(false);
            return undefined;
        }

        resumable.assignBrowse(browseButtonRef.current);
        resumableRef.current = resumable;
        setResumableReady(true);

        resumable.on('fileAdded', () => {
            setError('');
            setUploading(true);
            resumable.upload();
        });

        resumable.on('progress', () => {
            setUploadProgress(Math.floor(resumable.progress() * 100));
        });

        resumable.on('fileSuccess', async (_file, response) => {
            const payload = JSON.parse(response || '{}');

            if (payload?.managed_files_usage_bytes !== undefined) {
                setUsageBytes(payload.managed_files_usage_bytes);
            }
            if (payload?.managed_files_usage_label) {
                setUsageLabel(payload.managed_files_usage_label);
            }
            if (payload?.data) {
                setDocuments((currentDocuments) => {
                    const nextDocuments = currentDocuments.filter((document) => String(document.id) !== String(payload.data.id));
                    return [payload.data, ...nextDocuments];
                });
            }

            await loadDocuments();
        });

        resumable.on('fileError', (_file, message) => {
            setUploading(false);
            setUploadProgress(0);
            setError(extractUploadErrorMessage(message));
        });

        resumable.on('complete', async () => {
            setUploading(false);
            setUploadProgress(0);
            await loadDocuments();
        });

        return () => {
            setResumableReady(false);
            resumable.cancel();
            resumableRef.current = null;
        };
    }, [csrfToken, loadDocuments, uploadUrl]);

    const usageHint = managedFilesLimitBytes === null
        ? `${usageLabel} / ${managedFilesLimitLabel}`
        : `${usageLabel} / ${managedFilesLimitLabel}`;

    const filteredDocuments = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (normalizedSearch === '') {
            return documents;
        }

        return documents.filter((document) => {
            const haystack = [
                document.original_name,
                document.upload_name,
                document.file_type,
                document.status_label,
                document.filesize_label,
                document.device?.name,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(normalizedSearch);
        });
    }, [documents, searchTerm]);

    const handleDelete = useCallback(async (document) => {
        const confirmed = window.confirm(`Delete ${document.original_name}?`);
        if (!confirmed || !document.delete_url) {
            return;
        }

        setDeletingId(String(document.id));
        try {
            const response = await fetch(document.delete_url, {
                method: 'DELETE',
                headers: {
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                credentials: 'same-origin',
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message ?? 'Delete failed.');
            }

            await loadDocuments();
        } catch (deleteError) {
            setError(deleteError.message ?? 'Delete failed.');
        } finally {
            setDeletingId('');
        }
    }, [csrfToken, loadDocuments]);

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h6">OCR documents queue</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Upload PDF or image files, then open details to process and inspect the extracted content.
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Chip color="info" label={`User files: ${usageHint}`} />
                    <Button ref={browseButtonRef} variant="contained" color="secondary" onClick={resumableReady ? undefined : handlePickFiles} disabled={uploading}>
                        Upload files
                    </Button>
                    <input ref={inputRef} type="file" accept="application/pdf,image/*" multiple hidden onChange={resumableReady ? undefined : handleUpload} />
                </Stack>
            </Stack>

            {uploading && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary">Uploading... {uploadProgress}%</Typography>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
                <TextField
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search documents"
                    size="small"
                    sx={{ width: { xs: '100%', md: 320 } }}
                />
                {!loading && documents.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        Showing {filteredDocuments.length} of {documents.length} documents
                    </Typography>
                )}
            </Stack>

            {loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading OCR documents...</Typography>
                </Stack>
            ) : documents.length === 0 ? (
                <Alert severity="info">No OCR documents uploaded yet.</Alert>
            ) : filteredDocuments.length === 0 ? (
                <Alert severity="info">No OCR documents match the current search.</Alert>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Preview</TableCell>
                            <TableCell>Filename</TableCell>
                            <TableCell>Device</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDocuments.map((document) => (
                            <TableRow key={document.id} hover>
                                <TableCell sx={{ width: 96 }}>
                                    {document.thumb_url ? (
                                        <Box
                                            component="img"
                                            src={document.thumb_url}
                                            alt={document.original_name}
                                            sx={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 1 }}
                                        />
                                    ) : (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <DescriptionIcon color="action" />
                                            <Typography variant="caption">PDF</Typography>
                                        </Stack>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{document.original_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{document.upload_name}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {document.device?.name || 'Not selected'}
                                    </Typography>
                                </TableCell>
                                <TableCell>{document.file_type.toUpperCase()}</TableCell>
                                <TableCell>{document.filesize_label}</TableCell>
                                <TableCell>
                                    <Chip label={document.status_label} color={resolveStatusColor(document.status)} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            href={detailsUrlTemplate.replace('__ID__', String(document.id))}
                                        >
                                            Details
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="error"
                                            disabled={deletingId === String(document.id)}
                                            onClick={() => handleDelete(document)}
                                        >
                                            {deletingId === String(document.id) ? 'Deleting...' : 'Delete'}
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
};

const OcrDocumentDetailsPage = ({ detailsUrl, processUrl, backUrl, devices }) => {
    const [document, setDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [isOfficialDocument, setIsOfficialDocument] = useState(false);
    const [selectedResultPage, setSelectedResultPage] = useState(1);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewZoom, setPreviewZoom] = useState(1);
    const csrfToken = useMemo(() => getCsrfToken(), []);

    const loadDocument = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(detailsUrl, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Unable to load OCR document details.');
            }

            const payload = await response.json();
            const nextDocument = payload?.data ?? null;
            setDocument(nextDocument);
            setSelectedDeviceId(nextDocument?.device?.id ? String(nextDocument.device.id) : '');
            setIsOfficialDocument(Boolean(nextDocument?.is_official_document));
            setError('');
        } catch (requestError) {
            setError(requestError.message ?? 'Unable to load OCR document details.');
            setDocument(null);
        } finally {
            setLoading(false);
        }
    }, [detailsUrl]);

    useEffect(() => {
        loadDocument();
    }, [loadDocument]);

    useEffect(() => {
        if (document?.status !== 'processing' || !document?.id || !window.Echo) {
            return undefined;
        }

        const channelName = `public.ocr-documents.${document.id}`;
        const channel = window.Echo.channel(channelName);

        const listener = (payload) => {
            const nextStatus = String(payload?.status ?? '').toLowerCase();
            if (nextStatus === 'processed' || nextStatus === 'failed') {
                window.location.reload();
            }
        };

        channel.listen('ocr-document-updated', listener);

        return () => {
            if (window.Echo) {
                window.Echo.leaveChannel(channelName);
            }
        };
    }, [document?.id, document?.status]);

    useEffect(() => {
        if (document?.status !== 'processing' || window.Echo) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            loadDocument();
        }, 5000);

        return () => window.clearInterval(intervalId);
    }, [document?.status, loadDocument]);

    useEffect(() => {
        setSelectedResultPage((currentPage) => {
            if (!document?.result_payload?.pages?.length) {
                return 1;
            }

            return Math.min(currentPage, document.result_payload.pages.length);
        });
    }, [document?.result_payload?.pages]);

    const handleProcess = useCallback(async () => {
        if (!selectedDeviceId) {
            setError('Please select a processing device.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(processUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    device_id: Number(selectedDeviceId),
                    is_official_document: isOfficialDocument,
                }),
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message ?? 'Unable to start OCR processing.');
            }

            setDocument(payload?.data ?? document);
            setError('');
        } catch (requestError) {
            setError(requestError.message ?? 'Unable to start OCR processing.');
        } finally {
            setSubmitting(false);
        }
    }, [csrfToken, document, isOfficialDocument, processUrl, selectedDeviceId]);

    const selectedDevice = devices.find((device) => String(device.id) === selectedDeviceId) ?? null;
    const canSubmitProcess = Boolean(selectedDeviceId) && (selectedDevice?.active ?? false) && !submitting;

    if (loading) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading OCR document...</Typography>
            </Stack>
        );
    }

    if (!document) {
        return <Alert severity="error">Unable to load the OCR document.</Alert>;
    }

    const resultPayload = document.result_payload ?? null;
    const pages = Array.isArray(resultPayload?.pages) ? resultPayload.pages : [];
    const currentResultPage = pages.length > 0 ? pages[Math.max(0, selectedResultPage - 1)] : null;
    const processButtonLabel = document.status === 'processed' ? 'Reprocess document' : 'Process document';
    const previewTitle = document.file_type === 'pdf' ? 'PDF preview' : 'Image preview';

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h6">{document.original_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {document.filesize_label} • {document.file_type.toUpperCase()} • {document.status_label}
                    </Typography>
                </Box>
                <Button variant="outlined" href={backUrl}>Back to OCR documents</Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {document.status === 'processing' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    OCR processing is running. This page refreshes automatically when the worker response arrives.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                    <Accordion defaultExpanded disableGutters sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                                <Typography variant="subtitle1">Processing</Typography>
                                <Chip label={document.status_label} color={resolveStatusColor(document.status)} size="small" />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="ocr-device-label">Master device</InputLabel>
                                    <Select
                                        labelId="ocr-device-label"
                                        value={selectedDeviceId}
                                        label="Master device"
                                        onChange={(event) => setSelectedDeviceId(String(event.target.value))}
                                        disabled={submitting}
                                    >
                                        {devices.map((device) => (
                                            <MenuItem key={device.id} value={String(device.id)} disabled={!device.active}>
                                                {device.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedDevice && (
                                    <Alert severity={selectedDevice.active ? 'success' : 'warning'}>
                                        Selected device: {selectedDevice.name}
                                    </Alert>
                                )}

                                <Tooltip title="Enable this only for Romanian official documents such as ID cards, electronic ID cards, driving licences, vehicle registration certificates, or passports. For multi-page PDFs, turn it on only when every page belongs to the same official document type.">
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={isOfficialDocument}
                                                onChange={(event) => setIsOfficialDocument(event.target.checked)}
                                                disabled={submitting}
                                            />
                                        )}
                                        label="This document is an official document"
                                    />
                                </Tooltip>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleProcess}
                                    disabled={!canSubmitProcess}
                                >
                                    {submitting ? 'Sending...' : processButtonLabel}
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid item xs={12} lg={8}>
                    <Accordion defaultExpanded disableGutters sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                                <Box>
                                    <Typography variant="subtitle1">Source preview</Typography>
                                    <Typography variant="caption" color="text.secondary">Open a larger viewer for better readability.</Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    aria-label="Open source preview in fullscreen"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setPreviewDialogOpen(true);
                                    }}
                                >
                                    <OpenInFullIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ minHeight: 520, maxHeight: 620, overflow: 'hidden', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                                {document.file_type === 'pdf' ? (
                                    <Box
                                        component="iframe"
                                        src={document.file_url}
                                        title="OCR source preview"
                                        sx={{ width: '100%', height: 620, border: 0, backgroundColor: '#f5f5f5' }}
                                    />
                                ) : (
                                    <Box sx={{ height: 620, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: 2 }}>
                                        <CardMedia
                                            component="img"
                                            image={document.file_url}
                                            alt={document.original_name}
                                            sx={{ width: '100%', maxWidth: '100%', borderRadius: 1, objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1">Result</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {pages.length > 0 ? `${pages.length} page${pages.length === 1 ? '' : 's'} returned by OCR.` : 'The extracted OCR payload will appear here after processing completes.'}
                                    </Typography>
                                </Box>
                                {pages.length > 1 && (
                                    <Pagination
                                        count={pages.length}
                                        page={selectedResultPage}
                                        onChange={(_, page) => setSelectedResultPage(page)}
                                        color="primary"
                                        size="small"
                                    />
                                )}
                            </Stack>

                            {document.status === 'failed' && document.error_message && (
                                <Alert severity="error" sx={{ mb: 2 }}>{document.error_message}</Alert>
                            )}
                            {document.status !== 'processed' ? (
                                <Typography variant="body2" color="text.secondary">
                                    The extracted OCR payload will appear here after processing completes.
                                </Typography>
                            ) : pages.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No pages were returned by the OCR worker.
                                </Typography>
                            ) : (
                                <Stack spacing={2}>
                                    <Box sx={{ minHeight: 520, maxHeight: 620, overflow: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: '#fafafa', p: 2 }}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle2">Page {selectedResultPage}</Typography>
                                                {currentResultPage?.document && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Structured type: {currentResultPage.document.document_type || 'Unknown'}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {typeof currentResultPage?.text === 'string' && currentResultPage.text.trim() !== '' && (
                                                <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#fff', border: '1px solid', borderColor: 'divider' }}>
                                                    <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', m: 0 }}>
                                                        {currentResultPage.text}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {currentResultPage?.document && (
                                                <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#fff', border: '1px solid', borderColor: 'divider' }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="subtitle2">Structured document</Typography>
                                                        <Typography variant="body2">Type: {currentResultPage.document.document_type || 'Unknown'}</Typography>
                                                        {getOrderedFieldEntries(currentResultPage.document).length > 0 && (
                                                            <Table size="small">
                                                                <TableBody>
                                                                    {getOrderedFieldEntries(currentResultPage.document).map(({ name, value }) => (
                                                                        <TableRow key={name}>
                                                                            <TableCell sx={{ width: '34%', fontWeight: 600 }}>{name}</TableCell>
                                                                            <TableCell>{String(value ?? '')}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>

                                    {pages.length > 1 && (
                                        <Stack direction="row" justifyContent="flex-end">
                                            <Pagination
                                                count={pages.length}
                                                page={selectedResultPage}
                                                onChange={(_, page) => setSelectedResultPage(page)}
                                                color="primary"
                                                size="small"
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog
                open={previewDialogOpen}
                onClose={() => {
                    setPreviewDialogOpen(false);
                    setPreviewZoom(1);
                }}
                fullScreen
            >
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="subtitle1">{previewTitle}</Typography>
                            <Typography variant="caption" color="text.secondary">{document.original_name}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {document.file_type === 'image' && (
                                <>
                                    <IconButton onClick={() => setPreviewZoom((currentZoom) => Math.max(0.5, Number((currentZoom - 0.25).toFixed(2))))}>
                                        <ZoomOutIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setPreviewZoom(1)}>
                                        <RestartAltIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setPreviewZoom((currentZoom) => Math.min(4, Number((currentZoom + 0.25).toFixed(2))))}>
                                        <ZoomInIcon />
                                    </IconButton>
                                </>
                            )}
                            <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<CloseIcon />}
                                onClick={() => {
                                    setPreviewDialogOpen(false);
                                    setPreviewZoom(1);
                                }}
                            >
                                Close
                            </Button>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, backgroundColor: '#111' }}>
                    {document.file_type === 'pdf' ? (
                        <Box
                            component="iframe"
                            src={document.file_url}
                            title="OCR source fullscreen preview"
                            sx={{ width: '100%', height: 'calc(100vh - 96px)', border: 0, backgroundColor: '#f5f5f5' }}
                        />
                    ) : (
                        <Box sx={{ height: 'calc(100vh - 96px)', overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: 3 }}>
                            <Box
                                component="img"
                                src={document.file_url}
                                alt={document.original_name}
                                sx={{
                                    maxWidth: '100%',
                                    transform: `scale(${previewZoom})`,
                                    transformOrigin: 'top center',
                                    transition: 'transform 120ms ease-out',
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

function resolveStatusColor(status) {
    switch (status) {
        case 'processed':
            return 'success';
        case 'processing':
            return 'warning';
        case 'failed':
            return 'error';
        default:
            return 'default';
    }
}

OcrDocumentsPage.propTypes = {
    listUrl: PropTypes.string.isRequired,
    uploadUrl: PropTypes.string.isRequired,
    detailsUrlTemplate: PropTypes.string.isRequired,
    managedFilesUsageBytes: PropTypes.number,
    managedFilesUsageLabel: PropTypes.string,
    managedFilesLimitBytes: PropTypes.number,
    managedFilesLimitLabel: PropTypes.string,
};

OcrDocumentsPage.defaultProps = {
    managedFilesUsageBytes: 0,
    managedFilesUsageLabel: '0 B',
    managedFilesLimitBytes: null,
    managedFilesLimitLabel: 'Unlimited',
};

OcrDocumentDetailsPage.propTypes = {
    detailsUrl: PropTypes.string.isRequired,
    processUrl: PropTypes.string.isRequired,
    backUrl: PropTypes.string.isRequired,
    devices: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        active: PropTypes.bool,
    })),
};

OcrDocumentDetailsPage.defaultProps = {
    devices: [],
};

export { OcrDocumentsPage, OcrDocumentDetailsPage };